import {useState} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {
  OptionMarker,
  QuestionHeading,
} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  inkOn,
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayAccent,
  usePlayContentPadding,
  usePlayFooterStyle,
  usePlayOptionListStyle,
  usePlayOptionRowRhythm,
  usePlayOptionStyle,
  usePlayOptionTextStyle,
  usePlayPrimaryAction,
  usePlaySelectionMode,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookInkSound} from '#/components/flashbooks/useFlashBookInkSound'
import {SLIDE_TYPE_META} from '#/types/capsule'

/** Capsule accent for this block type — the fallback outside a book. */
const ACCENT = SLIDE_ACCENT.multiple_choice

export function MultiChoiceSlidePlay({
  slide,
  onAnswer,
  showFeedback,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const p = useFlashBookPlayPalette()
  // Inside a book every selection affordance wears the book's own accent.
  const accent = usePlayAccent(ACCENT)
  // The book page supplies its own margins; capsule play keeps its 24px.
  const padding = usePlayContentPadding(24)
  const optionStyle = usePlayOptionStyle()
  // On ruled paper the writing goes *on* the line, so the row and its spacing
  // come from the ruling rather than from the widget's own padding.
  const rowRhythm = usePlayOptionRowRhythm()
  const listStyle = usePlayOptionListStyle()
  const answerText = usePlayOptionTextStyle()
  // The space between the question and its answers is a whole number of the
  // page's rules, so the answers start on a line rather than 16px off one.
  const stackGap = usePlayStackGap(16)
  // The check button is furniture on the page: a whole number of rules tall.
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  // The strip it sits in is furniture too, and on ruled paper that means the
  // button alone, printed on the lines.
  const footerStyle = usePlayFooterStyle()
  // A paper theme marks the answer with a highlighter stroke over the words
  // rather than filling the row.
  const highlight = usePlaySelectionMode() === 'highlight'
  const ink = useInkStroke()
  const inkSound = useFlashBookInkSound()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)
  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setSelectedIds(new Set())
  }

  const options = slide.options ?? []
  const correctOptions = options.filter(o => o.isCorrect)

  const toggleOption = (id: string) => {
    if (showFeedback) return
    // Marking is the whole gesture here — there is no verdict until Check —
    // so this is the stroke the learner hears.
    inkSound.mark()
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleCheck = () => {
    if (selectedIds.size === 0) return
    const selectedCorrect = options.filter(
      o => selectedIds.has(o.id) && o.isCorrect,
    ).length
    const selectedWrong = options.filter(
      o => selectedIds.has(o.id) && !o.isCorrect,
    ).length
    const maxPoints = correctOptions.length
    const rawPoints = selectedCorrect - selectedWrong
    const points = Math.max(0, rawPoints)
    const isCorrect = points === maxPoints && selectedWrong === 0
    onAnswer({
      isCorrect,
      points,
      maxPoints,
      solutionText: correctOptions.map(o => o.label).join(', '),
      explanation: slide.explanation,
      userAnswer: Array.from(selectedIds),
    })
  }

  const getOptionStyle = (optId: string, isCorrect: boolean) => {
    const isSelected = selectedIds.has(optId)
    // In highlight mode the stroke on the words is the mark, so the row keeps
    // the paper showing through it and no fill is laid over the ruling.
    const fill = (tone: string) =>
      highlight ? null : {borderColor: tone, backgroundColor: tone + '22'}
    if (showFeedback) {
      if (isCorrect) return [optionStyle, rowRhythm, fill(p.positive)]
      if (isSelected && !isCorrect)
        return [optionStyle, rowRhythm, fill(p.negative)]
    }
    if (isSelected) return [optionStyle, rowRhythm, fill(accent)]
    return [optionStyle, rowRhythm]
  }

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={{padding, gap: stackGap}}>
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>

        <View style={[ss.optList, listStyle]}>
          {options.map((opt, i) => {
            const isSelected = selectedIds.has(opt.id)
            const tone = showFeedback
              ? opt.isCorrect
                ? p.positive
                : isSelected
                  ? p.negative
                  : p.muted
              : isSelected
                ? accent
                : p.muted
            return (
              <TouchableOpacity
                key={opt.id}
                style={getOptionStyle(opt.id, opt.isCorrect)}
                onPress={() => toggleOption(opt.id)}
                accessibilityRole="checkbox"
                accessibilityLabel={opt.label}
                accessibilityHint={l`Adds or removes this answer from your selection`}
                accessibilityState={{checked: isSelected}}>
                {/* Multi-select marks a tick inside the theme's marker, so a
                    checkbox is still readable as a checkbox in every theme. */}
                <View style={ss.markerWrap}>
                  <OptionMarker
                    index={i}
                    tone={tone}
                    selected={isSelected || (showFeedback && opt.isCorrect)}
                  />
                  {isSelected && (
                    <View style={[ss.tickOverlay, {backgroundColor: tone}]}>
                      <Feather name="check" size={11} color={inkOn(tone)} />
                    </View>
                  )}
                </View>
                <Text style={[ss.optLabel, answerText, {color: p.body}]}>
                  <Text
                    style={ink(
                      isSelected || (showFeedback && opt.isCorrect)
                        ? tone
                        : null,
                    )}>
                    {opt.label}
                  </Text>
                </Text>
              </TouchableOpacity>
            )
          })}
        </View>
      </PlayScroller>

      {!showFeedback && (
        <View style={[ss.footer, {borderTopColor: p.border}, footerStyle]}>
          <TouchableOpacity
            style={[
              ss.checkBtn,
              {
                backgroundColor:
                  selectedIds.size > 0 ? primaryColor : p.surfaceAlt,
              },
              action.style,
            ]}
            onPress={handleCheck}
            disabled={selectedIds.size === 0}
            accessibilityRole="button"
            accessibilityLabel={l`Check answer`}
            accessibilityHint={undefined}>
            <Text
              style={[
                ss.checkBtnText,
                {
                  color:
                    action.ink(selectedIds.size > 0) ??
                    (selectedIds.size === 0 ? p.muted : p.onAccent),
                },
                action.labelStyle(selectedIds.size > 0),
              ]}>
              <Trans>Check</Trans>
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  )
}

const ss = StyleSheet.create({
  root: {flex: 1},
  scroll: {flex: 1},
  scrollContent: {padding: 24, gap: 16},
  question: {
    fontSize: 20,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    lineHeight: 28,
    marginTop: 8,
  },
  optList: {gap: 10, marginTop: 4},
  opt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: PLAY_SURFACE.border,
    backgroundColor: PLAY_SURFACE.surface,
  },
  markerWrap: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tickOverlay: {
    position: 'absolute',
    width: 17,
    height: 17,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optLabel: {flex: 1, fontSize: 15, lineHeight: 21},
  explanationBox: {
    padding: 14,
    backgroundColor: PLAY_SURFACE.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PLAY_SURFACE.border,
  },
  explanationText: {
    fontSize: 14,
    color: PLAY_SURFACE.textMuted,
    lineHeight: 20,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: PLAY_SURFACE.border,
  },
  checkBtn: {borderRadius: 14, paddingVertical: 15, alignItems: 'center'},
  checkBtnText: {color: '#fff', fontSize: 16, fontWeight: '700'},
  checkBtnDisabled: {color: 'rgba(255,255,255,0.4)'},
})
